"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PublicCommunityCartAddItemRequest,
  PublicCommunityCartResponse,
} from "@repo/global-types-buddify/community";
import {
  addCommunityCartItem,
  createCommunityCartCheckout,
  fetchCommunityCart,
  removeCommunityCartItem,
  updateCommunityCartItem,
} from "@/lib/community/cart-api";

const CART_QUERY_KEY = ["community-cart"] as const;

interface CommunityCartContextValue {
  cart: PublicCommunityCartResponse | undefined;
  totalItems: number;
  isMutating: boolean;
  isError: boolean;
  refetchCart: () => void;
  addItem: (input: PublicCommunityCartAddItemRequest) => Promise<void>;
  updateItem: (id: string, quantity: number) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  checkout: (slug: string) => Promise<string>;
}

const CommunityCartContext = createContext<CommunityCartContextValue | null>(null);

export function CommunityCartProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data: cart, isError, refetch } = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: fetchCommunityCart,
    staleTime: 30_000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });

  const addMutation = useMutation({
    mutationFn: addCommunityCartItem,
    onSuccess: (data) => queryClient.setQueryData(CART_QUERY_KEY, data.cart),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) =>
      updateCommunityCartItem(id, quantity),
    onSuccess: (data) => queryClient.setQueryData(CART_QUERY_KEY, data),
  });
  const removeMutation = useMutation({
    mutationFn: removeCommunityCartItem,
    onSuccess: (data) => queryClient.setQueryData(CART_QUERY_KEY, data),
  });
  const checkoutMutation = useMutation({
    mutationFn: createCommunityCartCheckout,
  });

  const value = useMemo<CommunityCartContextValue>(
    () => ({
      cart,
      totalItems: cart?.total_items ?? 0,
      isMutating:
        addMutation.isPending ||
        updateMutation.isPending ||
        removeMutation.isPending ||
        checkoutMutation.isPending,
      isError,
      refetchCart: () => {
        void refetch();
      },
      addItem: async (input) => {
        await addMutation.mutateAsync(input);
      },
      updateItem: async (id, quantity) => {
        await updateMutation.mutateAsync({ id, quantity });
        await invalidate();
      },
      removeItem: async (id) => {
        await removeMutation.mutateAsync(id);
      },
      checkout: async (slug) => {
        const result = await checkoutMutation.mutateAsync(slug);
        return result.checkout_url;
      },
    }),
    [cart, isError, refetch, addMutation, updateMutation, removeMutation, checkoutMutation],
  );

  return (
    <CommunityCartContext.Provider value={value}>
      {children}
    </CommunityCartContext.Provider>
  );
}

export function useCommunityCart(): CommunityCartContextValue {
  const context = useContext(CommunityCartContext);
  if (!context) {
    throw new Error("useCommunityCart must be used within CommunityCartProvider");
  }
  return context;
}
